import React, { useState } from 'react'

import { getErrorMessage, normalizeShopifyDomain } from 'utils'

import { useSnackbar } from 'contexts'
import { useConnectShopify } from 'hooks/api'

import { Stack, Typography, Tooltip } from '@mui/material'
import { InfoIcon } from 'lucide-react'

import { Modal, Button, Alert, Input } from '@repo/ui'

interface ConnectShopifyModalProps {
  integration?: any
  open: boolean
  onClose: () => void
  refetch?: () => void
}

export const ConnectShopifyModal = (props: ConnectShopifyModalProps) => {
  const { integration, open, onClose = () => { }, refetch } = props
  const [shopDomain, setShopDomain] = useState('')

  const { open: notification } = useSnackbar()

  const { connectShopify, isConnecting, connectionError } = useConnectShopify()

  const handleConnect = async () => {
    if (!shopDomain.trim()) {
      notification({
        type: 'error',
        title: 'Please enter your Shopify store domain',
      })
      return
    }

    // VRG-724 — accept what a merchant actually pastes (`https://X.myshopify.com/`,
    // `x.myshopify.com/admin`, the admin URL) and send the normalised `<handle>.myshopify.com`. A
    // custom storefront domain is not accepted: the API only ever talks to the myshopify host.
    const normalizedDomain = normalizeShopifyDomain(shopDomain)
    if (!normalizedDomain) {
      notification({
        type: 'error',
        title: 'Please enter a valid Shopify domain (e.g., your-store.myshopify.com)',
      })
      return
    }

    try {
      const response = await connectShopify({
        shop_domain: normalizedDomain,
        origin: 'integrations'
      })

      notification({
        type: 'success',
        title: 'Redirecting to Shopify for authorization...',
      })

      handleClose()

      // Redirect to Shopify OAuth URL
      window.location.href = response.auth_url

    } catch (error) {
      const message = getErrorMessage(error)
      notification({
        type: 'error',
        title: message,
      })
    }
  }

  const handleClose = () => {
    setShopDomain('')
    onClose()
  }

  const isValidDomain = (domain: string) => normalizeShopifyDomain(domain) !== null

  const errorValue = !!shopDomain && !isValidDomain(shopDomain)

  return (
    <Modal
      title="Connect to Shopify"
      open={open}
      onClose={handleClose}
      actions={
        <>
          <Button
            disableElevation
            variant="secondary"
            onClick={handleClose}
            disabled={isConnecting}
          >
            Cancel
          </Button>

          <Button
            variant="primary"
            disableElevation
            onClick={handleConnect}
            loading={isConnecting}
            disabled={!shopDomain || !shopDomain.trim() || !isValidDomain(shopDomain)}
          >
            {isConnecting ? 'Connecting...' : 'Connect'}
          </Button>
        </>
      }
    >
      <Stack spacing={3}>
        <Typography variant="body1" color="text.secondary" sx={{ fontSize: '14px' }}>
          Enter your Shopify store domain to connect your store with Vera.
        </Typography>

        <Stack spacing={1}>
          <Stack direction="row" alignItems="center" spacing={2} pb={1}>
            <Typography variant="body3">MyShopify domain</Typography>
            <Tooltip title="Enter your store's myshopify.com domain (e.g., your-store.myshopify.com)">
              <InfoIcon size={16} style={{ cursor: 'pointer', opacity: 0.6 }} />
            </Tooltip>
          </Stack>
          <Input
            placeholder="your-store.myshopify.com"
            value={shopDomain}
            onChange={(value) => setShopDomain(value.replace(/^https?:\/\//, ''))}
            error={errorValue}
            helperText={shopDomain && !isValidDomain(shopDomain) ? 'Please enter a valid Shopify domain' : ''}
          />
        </Stack>

        <Alert
          severity="info"
          description="You will be redirected to Shopify to authorize the connection. Make sure you have admin access to your Shopify store."
        />
      </Stack>
    </Modal>
  )
}
